import { useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { timeAgo } from "src/reusable/Functions";
import type { ConversationHistoryItem } from "src/api/conversationHistory";
import ChatShareModal from "src/views/assistant/components/ChatShareModal";
import { conversationShareUrl } from "./conversationPath";
import { ResourceStatusIndicator } from "src/features/notifications";

interface ConversationHistoryResultsProps {
  items: ConversationHistoryItem[];
  onOpen: (item: ConversationHistoryItem) => void;
}

const HistoryRow = ({
  item,
  onOpen,
  onShare,
}: {
  item: ConversationHistoryItem;
  onOpen: (item: ConversationHistoryItem) => void;
  onShare: (item: ConversationHistoryItem) => void;
}) => (
  <ListItemButton
    component="li"
    data-testid={`conversation-history-item-${item.id}`}
    data-attr="conversation-history:item:open-conversation"
    onClick={() => onOpen(item)}
  >
    <ListItemText
      primary={item.name || "Untitled conversation"}
      primaryTypographyProps={{ noWrap: true }}
      secondary={item.summary || undefined}
      secondaryTypographyProps={{ noWrap: true }}
    />
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        ml: 2,
        flexShrink: 0,
      }}
    >
      <ResourceStatusIndicator type="dialogue" id={item.id} />
      {item.in_project && item.project_folder_name && (
        <Chip
          size="small"
          variant="outlined"
          icon={<FolderOutlinedIcon />}
          label={item.project_folder_name}
        />
      )}
      {/* timeAgo, not a raw date format: Dialogues.create_date is a naive
          column, so it serializes without a Z and any parser reads it as
          local time — an off-by-one day near midnight. timeAgo appends the Z
          first, and it is what every other dialogue timestamp uses. */}
      <Typography variant="caption" color="text.secondary">
        {timeAgo(item.create_date)}
      </Typography>
      <Tooltip title="Share">
        <IconButton
          size="small"
          aria-label={`Share ${item.name || "Untitled conversation"}`}
          data-testid={`conversation-history-share-${item.id}`}
          data-attr="conversation-history:btn:share-conversation"
          // The whole row is the open action, so a press here has to be
          // claimed before it reaches the row and navigates away from the
          // modal that is about to show the link.
          onClick={(event) => {
            event.stopPropagation();
            onShare(item);
          }}
          sx={{ color: "text.secondary" }}
        >
          <ShareOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  </ListItemButton>
);

/**
 * The loaded history items, in the order the server returned — recency, or
 * relevance on the search path. Which group they belong to (In projects /
 * General) is a scope the caller selected before fetching, not something
 * split out here: see ConversationHistoryModal's scope tabs.
 *
 * Every row offers Share with no ownership check: this endpoint lists the
 * signed-in user's own conversations, so there is no one else's chat here to
 * re-share.
 */
export default function ConversationHistoryResults({
  items,
  onOpen,
}: ConversationHistoryResultsProps) {
  // One modal for the list rather than one per row: the rows are a pageable
  // feed, and only one chat can be being shared at a time.
  const [sharing, setSharing] = useState<ConversationHistoryItem | null>(null);

  const shareModal = sharing && (
    <ChatShareModal
      dialogueId={sharing.id}
      shareUrl={conversationShareUrl(sharing)}
      open
      onClose={() => setSharing(null)}
    />
  );

  return (
    <List disablePadding data-testid="conversation-history-results">
      {items.map((item) => (
        <HistoryRow
          key={item.id}
          item={item}
          onOpen={onOpen}
          onShare={setSharing}
        />
      ))}
      {shareModal}
    </List>
  );
}
